const groupPrefix = createApiRoute(ApiModule.PURCHASE, ApiAuthorization.EMPLOYEE, ApiDataModel.ORDERS)
const purchasePrefix = createApiRoute(ApiModule.PURCHASE, ApiAuthorization.EMPLOYEE)
const searchPrefix = createApiRoute(ApiModule.WAREHOUSE, ApiAuthorization.EMPLOYEE)
const suppliersPrefix = createApiRoute(ApiModule.SUPPLIER, ApiAuthorization.EMPLOYEE)

const normalizeCategoryList = (data: unknown): Array<{ label: string; value: string }> => {
	let rawList: unknown[]
	if (Array.isArray(data)) {
		rawList = data
	} else if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown[] }).data)) {
		rawList = (data as { data: unknown[] }).data
	} else {
		rawList = []
	}

	return rawList
		.map(item => {
			if (typeof item === 'string') {
				return { label: item, value: item }
			}

			if (item && typeof item === 'object') {
				const record = item as Record<string, unknown>
				const value = (record.value as string) ?? (record.category as string) ?? (record.name as string) ?? null
				const label = (record.label as string) ?? value

				if (!value || !label) {
					return null
				}

				return { label, value }
			}

			return null
		})
		.filter((item): item is { label: string; value: string } => Boolean(item))
}

const normalizeOffsetRules = (data: unknown): IDeliveryOffsetRuleModel[] => {
	if (Array.isArray(data)) {
		return data.map(rule => DeliveryOffsetRule(rule as IDeliveryOffsetRule))
	}

	if (data && typeof data === 'object' && Array.isArray((data as { rules?: unknown[] }).rules)) {
		return (data as { rules: IDeliveryOffsetRule[] }).rules.map(rule => DeliveryOffsetRule(rule))
	}

	return []
}

export const apiOrders = {
	getPurchaseOrders: async (): Promise<IPurchaseOrderModel[]> => {
		const { data } = await apiCommon<IPurchaseOrder[]>(`${groupPrefix}`, {
			method: 'GET',
		})

		return data.map(d => PurchaseOrder(d))
	},

	createNewPurchaseOrder: async (payload: ICreatePurchaseOrderRequest) => {
		await apiCommon(`${groupPrefix}`, {
			method: 'POST',
			...(Object.keys(payload).length > 0 && { body: payload }),
		})
	},

	getPurchaseOrdersPaginated: async (
		page: number = 1,
		perPage: number = 25,
		search?: string,
		facilityId?: string,
	): Promise<{ data: IPurchaseOrderModel[]; meta: IApiMetaModel | undefined }> => {
		const { data, meta } = await apiCommon<IPurchaseOrder[]>(`${groupPrefix}`, {
			method: 'GET',
			...createQuery({ page, perPage }, { search, facility_id: facilityId }),
		})

		return {
			data: data.map(d => PurchaseOrder(d)),
			meta,
		}
	},

	getHistoryPurchaseOrdersPaginated: async (
		page: number = 1,
		perPage: number = 25,
		search?: string,
		facilityId?: string,
	): Promise<{ data: IPurchaseOrderModel[]; meta: IApiMetaModel | undefined }> => {
		const { data, meta } = await apiCommon<IPurchaseOrder[]>(`${groupPrefix}/completed`, {
			method: 'GET',
			...createQuery({ page, perPage }, { search, facility_id: facilityId }),
		})

		return {
			data: data.map(d => PurchaseOrder(d)),
			meta,
		}
	},

	checkExistingPurchaseOrder: async (id: number): Promise<boolean> => {
		const { data } = await apiCommon<boolean>(
			`${groupPrefix}/exists-for-production-plan?production_plan_id=${id}`,
			{
				method: 'GET',
			},
		)

		return data
	},

	sendSupplierProductSummary: async (suppliers: IAsignSupplierModel[]) => {
		const { data } = await apiCommon<IAsignSupplier[]>(`${groupPrefix}/items/assign-supplier`, {
			method: 'POST',
			body: suppliers.map(d => AsignSupplierToJson(d)),
		})

		return data
	},

	fetchProductsBySupplier: async (
		id: number,
		params?: {
			supplierId?: number
			supplierOrderSequence?: number
		},
	) => {
		const { data } = await apiCommon<IProductsBySupplier[]>(`${groupPrefix}/${id}/products-by-supplier`, {
			method: 'GET',
			...createQuery(undefined, undefined, undefined, {
				supplier_id: params?.supplierId,
				supplier_order_sequence: params?.supplierOrderSequence,
			}),
		})

		return data.map(d => ProductsBySupplier(d))
	},

	fetchRemovalReasons: async (): Promise<IRemovalReasonOption[]> => {
		const { data } = await apiCommon<IRemovalReasonOption[]>(`${groupPrefix}/removal-reasons`, {
			method: 'GET',
		})
		return data ?? []
	},

	deleteProductFromOrder: async (id: number, reasonCode: string, reasonNote?: string | null) =>
		await apiCommon(`${groupPrefix}/items/${id}`, {
			method: 'DELETE',
			body: {
				reason_code: reasonCode,
				...(reasonNote ? { reason_note: reasonNote } : {}),
			},
		}),

	deleteProductsFromOrder: async (
		purchaseOrderId: number,
		itemIds: number[],
		reasonCode: string,
		reasonNote?: string | null,
	) => {
		await apiCommon(`${groupPrefix}/${purchaseOrderId}/items`, {
			method: 'DELETE',
			body: {
				item_ids: itemIds,
				reason_code: reasonCode,
				...(reasonNote ? { reason_note: reasonNote } : {}),
			},
		})
	},

	acceptProduct: async (
		supplierOrderId: number | null | undefined,
		products: { productId: number; isAccepted: boolean; isChange?: boolean }[],
		purchaseOrderId: number,
	) => {
		if (supplierOrderId === null || supplierOrderId === undefined) {
			throw new Error('Brak ID zlecenia dostawcy do akceptacji produktów.')
		}

		await apiOrders.acceptProducts(purchaseOrderId, [
			{
				supplierOrderId,
				products,
			},
		])
	},

	acceptProducts: async (
		purchaseOrderId: number,
		supplierOrders: Array<{
			supplierOrderId: number | null | undefined
			products: { productId: number; isAccepted: boolean; isChange?: boolean }[]
		}>,
	) => {
		const normalizedSupplierOrders = supplierOrders
			.filter(
				supplierOrder => supplierOrder.supplierOrderId !== null && supplierOrder.supplierOrderId !== undefined,
			)
			.map(supplierOrder => ({
				supplier_order_id: supplierOrder.supplierOrderId as number,
				products: supplierOrder.products.map(product => ({
					productId: product.productId,
					isAccepted: product.isAccepted,
					...(product.isChange !== undefined ? { isChange: product.isChange } : {}),
				})),
			}))
			.filter(supplierOrder => supplierOrder.products.length > 0)

		if (!normalizedSupplierOrders.length) {
			throw new Error('Brak produktów do akceptacji.')
		}

		await apiCommon<string>(`${groupPrefix}/${purchaseOrderId}/supplier-orders/products/acceptance`, {
			method: 'PATCH',
			body: {
				supplier_orders: normalizedSupplierOrders,
			},
		})
	},

	acceptSupplierOffer: async (supplierOrderId: number | null | undefined, purchaseOrderId: number) => {
		if (supplierOrderId === null || supplierOrderId === undefined) {
			throw new Error('Brak ID zlecenia dostawcy do akceptacji zamówienia.')
		}

		await apiOrders.acceptSupplierOffers(purchaseOrderId, [supplierOrderId])
	},

	acceptSupplierOffers: async (purchaseOrderId: number, supplierOrderIds: Array<number | null | undefined>) => {
		const normalizedSupplierOrderIds = supplierOrderIds.filter(
			(id): id is number => id !== null && id !== undefined,
		)

		if (!normalizedSupplierOrderIds.length) {
			throw new Error('Brak ID zlecenia dostawcy do akceptacji zamówienia.')
		}

		await apiCommon<string>(`${groupPrefix}/${purchaseOrderId}/supplier-orders/accept`, {
			method: 'POST',
			body: {
				supplier_order_ids: normalizedSupplierOrderIds,
			},
		})
	},

	assignDefaultSupplier: async (itemId: number, supplierId: number) => {
		await apiCommon(`${groupPrefix}/items/${itemId}/default-supplier`, {
			method: 'PATCH',
			body: {
				supplier_id: supplierId,
			},
		})
	},

	updateItemOrderedQuantity: async (itemId: number, orderedQuantity: number): Promise<IPurchaseOrderModel> => {
		const { data } = await apiCommon<IPurchaseOrder>(`${groupPrefix}/items/${itemId}`, {
			method: 'PATCH',
			body: {
				quantity: orderedQuantity,
			},
		})

		return PurchaseOrder(data)
	},

	updateItemDeliveryDeadline: async (itemId: number, deliveryDeadline: string): Promise<IPurchaseOrderModel> => {
		const { data } = await apiCommon<IPurchaseOrder>(`${groupPrefix}/items/${itemId}/delivery-deadline`, {
			method: 'PATCH',
			body: {
				delivery_deadline: deliveryDeadline,
			},
		})

		return PurchaseOrder(data)
	},

	updatePurchaseOrderDeliveryDeadline: async (purchaseOrderId: number, deliveryDeadline: string) => {
		await apiCommon(`${groupPrefix}/${purchaseOrderId}`, {
			method: 'PUT',
			body: {
				delivery_deadline: deliveryDeadline,
			},
		})
	},

	createSupplierOrders: async (purchaseOrderId: number, itemIds: number[]) => {
		await apiCommon(`${groupPrefix}/${purchaseOrderId}/supplier-orders`, {
			method: 'POST',
			body: {
				item_ids: itemIds,
			},
		})
	},

	getPurchaseOrder: async (id: number, withDeleted = false): Promise<IPurchaseOrderModel> => {
		const { data } = await apiCommon<IPurchaseOrder>(`${groupPrefix}/${id}`, {
			method: 'GET',
			...createQuery(undefined, undefined, undefined, {
				with_deleted: withDeleted || undefined,
			}),
		})

		return PurchaseOrder(data)
	},

	addProductToPurchaseOrder: async (purchaseOrder: number, rawMaterialId: number) => {
		const { data } = await apiCommon(`${groupPrefix}/${purchaseOrder}/items`, {
			method: 'POST',
			body: {
				raw_material_id: rawMaterialId,
			},
		})

		return data
	},

	searchProductToAddOnPurchaseOrder: async (search: string) => {
		const { data } = await apiCommon(`${searchPrefix}/raw-materials?query=${search}`, {
			method: 'GET',
		})

		return data
	},
	fetchRawMaterialSuppliers: async (crmRawMaterialId: number): Promise<IRawMaterialSupplierModel[]> => {
		const { data } = await apiCommon<IRawMaterialSupplier[]>(
			`${suppliersPrefix}/raw-materials/${crmRawMaterialId}/suppliers`,
			{
				method: 'GET',
			},
		)

		return data.map(supplier => RawMaterialSupplier(supplier))
	},

	createManualSupplierProduct: async (
		payload: ICreateManualSupplierProductPayload,
	): Promise<IManualSupplierProductResponse> => {
		const { data } = await apiCommon<IManualSupplierProductResponse>(`${suppliersPrefix}/products/manual`, {
			method: 'POST',
			body: payload,
		})

		return data
	},

	fetchPurchaseRawMaterialCategories: async (): Promise<Array<{ label: string; value: string }>> => {
		const { data } = await apiCommon<unknown>(`${purchasePrefix}/raw-material-categories`, {
			method: 'GET',
		})

		return normalizeCategoryList(data)
	},

	fetchDeliveryOffsetRules: async (): Promise<IDeliveryOffsetRuleModel[]> => {
		const { data } = await apiCommon<unknown>(`${purchasePrefix}/delivery-offset-rules`, {
			method: 'GET',
		})

		return normalizeOffsetRules(data)
	},

	updateDeliveryOffsetRules: async (rules: IDeliveryOffsetRuleModel[]) => {
		await apiCommon(`${purchasePrefix}/delivery-offset-rules`, {
			method: 'PUT',
			body: {
				rules: rules.map(rule => DeliveryOffsetRuleToJson(rule)),
			},
		})
	},

	deleteDeliveryOffsetRule: async (id: number) => {
		await apiCommon(`${purchasePrefix}/delivery-offset-rules/${id}`, {
			method: 'DELETE',
		})
	},

	replaceTheProductWithAnAlternativeProduct: async (
		currentItemId: number,
		rawMaterialId: number,
	): Promise<IAlternativeProductResponseModel> => {
		const { data } = await apiCommon<IAlternativeProductResponse>(
			`${groupPrefix}/items/${currentItemId}/alternative`,
			{
				method: 'POST',
				body: {
					raw_material_id: rawMaterialId,
				},
			},
		)

		return AlternativeProductResponse(data)
	},

	restoreAlternativeProduct: async (currentItemId: number): Promise<IRestoredProductResponseModel> => {
		const { data } = await apiCommon<IRestoredProductResponse>(
			`${groupPrefix}/items/${currentItemId}/restore-main`,
			{
				method: 'POST',
			},
		)

		return RestoredProductResponse(data)
	},

	getMergeCandidates: async (
		excludeId?: number,
		search?: string,
		perPage: number = 15,
	): Promise<{ data: IPurchaseOrderModel[]; meta: IApiMetaModel | undefined }> => {
		const simpleQueries: Record<string, string | undefined> = {}
		if (excludeId) simpleQueries.exclude_id = `${excludeId}`

		const { data, meta } = await apiCommon<IPurchaseOrder[]>(`${groupPrefix}/merge-candidates`, {
			method: 'GET',
			...createQuery(
				{ page: 1, perPage },
				search ? { search } : undefined,
				undefined,
				Object.keys(simpleQueries).length > 0 ? simpleQueries : undefined,
			),
		})

		return {
			data: data.map(d => PurchaseOrder(d)),
			meta,
		}
	},

	mergeOrders: async (payload: IMergeOrdersRequest): Promise<IPurchaseOrderModel> => {
		const { data } = await apiCommon<IPurchaseOrder>(`${groupPrefix}/merge`, {
			method: 'POST',
			body: payload,
		})

		return PurchaseOrder(data)
	},

	getMergeBreakdown: async (id: number): Promise<IMergeBreakdownModel> => {
		const { data } = await apiCommon<IMergeBreakdown>(`${groupPrefix}/${id}/merge-breakdown`, {
			method: 'GET',
		})

		return {
			purchaseOrderId: data.purchase_order_id,
			name: data.name,
			mergeSources: data.merge_sources.map(s => ({
				mergeId: s.merge_id,
				purchaseOrderId: s.purchase_order_id,
				name: s.name,
				mergedAt: s.merged_at,
			})),
		}
	},
}
